/**
 * useDocumentTabs - Document Tab Management Hook
 * 
 * Extracts tab management logic from WorkspacePage.tsx:
 * - Tab close/close all functionality
 * - Back navigation with history tracking
 * - Viewer width resize state and handlers
 * - Tab history management
 */

import { useRef, useState, useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';

export interface OpenedDocumentForTabs {
  tabId: number;
  type?: string;
  documentNumber?: string;
  planDocument?: { id: string };
}

export interface ActiveEditDocumentForTabs {
  tabId?: number;
}

interface UseDocumentTabsDeps {
  // From AIContext
  activeTabId: number | null;
  setActiveTabId: (id: number | null) => void;
  openedDocuments: OpenedDocumentForTabs[];
  removeOpenedDocument: (tabId: number) => void;
  clearOpenedDocuments: () => void;
  activeEditDocument: ActiveEditDocumentForTabs | null;
  
  // Project Workspace (for project-specific persistence)
  activeProjectId?: string | null;
  
  // Viewer constraints
  viewerMinWidth?: number;
  viewerMaxWidth?: number;
  
  // Document lock release (for Agent mode pairing)
  releaseDocumentLock?: (documentId: string) => void;
  clearActiveEditDocument?: () => void;
  
  // Confirmation callback for closing edited documents (from useConfirm hook)
  confirmCloseEditing?: (options: { title: string; description: string }) => Promise<boolean>;
}

interface UseDocumentTabsReturn {
  // Tab history
  tabHistoryRef: React.MutableRefObject<number[]>;
  
  // Viewer resize state
  viewerWidth: number;
  setViewerWidth: (width: number) => void;
  isResizingViewer: boolean;
  setIsResizingViewer: (val: boolean) => void;
  
  // Tab actions
  closeTab: (tabId: number) => void;
  closeAll: () => void;
  goBack: (tabId: number) => void;
  
  // State persistence
  saveViewerState: () => void;
}

export function useDocumentTabs(deps: UseDocumentTabsDeps): UseDocumentTabsReturn {
  const {
    activeTabId,
    setActiveTabId,
    openedDocuments,
    removeOpenedDocument,
    clearOpenedDocuments,
    activeEditDocument,
    activeProjectId,
    viewerMinWidth = 200,
    viewerMaxWidth = 2000,
    releaseDocumentLock,
    clearActiveEditDocument,
    confirmCloseEditing,
  } = deps;

  // Tab navigation history
  const tabHistoryRef = useRef<number[]>([]);
  
  // Generate localStorage keys based on project context
  const getStorageKey = useCallback((baseKey: string) => {
    if (activeProjectId) {
      return `${baseKey}-${activeProjectId}`;
    }
    return `${baseKey}-global`;
  }, [activeProjectId]);

  // Viewer resize state
  const [viewerWidth, setViewerWidth] = useState(() => {
    const saved = localStorage.getItem('aeroViewerWidth');
    return saved ? parseInt(saved, 10) : 600;
  });
  const [isResizingViewer, setIsResizingViewer] = useState(false);

  // Track tab navigation history for back button
  useEffect(() => {
    if (activeTabId !== null) {
      const history = tabHistoryRef.current;
      // Don't add duplicates if already the most recent
      if (history[history.length - 1] !== activeTabId) {
        // Add to history, keep max 50 entries
        tabHistoryRef.current = [...history, activeTabId].slice(-50);
      }
    }
  }, [activeTabId]);

  // Save viewer state to localStorage (project-specific)
  const saveViewerState = useCallback(() => {
    try {
      // Use project-specific keys for opened documents and active tab
      const docsKey = getStorageKey('aeroOpenedDocuments');
      const tabKey = getStorageKey('aeroActiveTabId');
      
      localStorage.setItem(docsKey, JSON.stringify(openedDocuments));
      localStorage.setItem(tabKey, activeTabId?.toString() || '');
      // Viewer width is global (not project-specific)
      localStorage.setItem('aeroViewerWidth', viewerWidth.toString());
    } catch (error) {
      logger.error('Failed to save viewer state:', error);
    }
  }, [openedDocuments, activeTabId, viewerWidth, getStorageKey]);

  // Save state when documents or active tab changes
  useEffect(() => {
    if (openedDocuments.length > 0) {
      saveViewerState();
    }
  }, [openedDocuments, activeTabId, saveViewerState]);

  // Viewer resize handlers
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isResizingViewer) {
        // Calculate new width (from left edge)
        // Account for sidebar width (~64px) and ensure chat area has minimum space (350px)
        const sidebarWidth = 64;
        const chatMinWidth = 350;
        const maxAllowedWidth = window.innerWidth - sidebarWidth - chatMinWidth - 10;
        
        const newWidth = e.clientX - sidebarWidth;
        const constrainedWidth = Math.max(
          viewerMinWidth, 
          Math.min(viewerMaxWidth, Math.min(maxAllowedWidth, newWidth))
        );
        setViewerWidth(constrainedWidth);
        localStorage.setItem('aeroViewerWidth', constrainedWidth.toString());
      }
    };

    const handleMouseUp = () => {
      if (isResizingViewer) {
        setIsResizingViewer(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    if (isResizingViewer) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingViewer, viewerMinWidth, viewerMaxWidth]);

  /**
   * Close a single tab
   */
  const closeTab = useCallback(async (tabId: number) => {
    const doc = openedDocuments.find(d => d.tabId === tabId);
    if (!doc) return;
    
    // Check if this is the editing document
    if (activeEditDocument && activeEditDocument.tabId === tabId) {
      // Use provided confirmation callback, or fallback to native confirm
      const shouldClose = confirmCloseEditing
        ? await confirmCloseEditing({
            title: 'Close Edited Document',
            description: 'This document is being edited. Close anyway? Unsaved changes will be lost.',
          })
        : window.confirm('This document is being edited. Close anyway?');
      
      if (!shouldClose) {
        return;
      }
      // Release the document lock when closing an edited document tab
      if (doc.documentNumber && releaseDocumentLock) {
        logger.debug('[useDocumentTabs] Releasing document lock for closed tab:', doc.documentNumber);
        releaseDocumentLock(doc.documentNumber);
      }
      // Clear the active edit document
      if (clearActiveEditDocument) {
        clearActiveEditDocument();
      }
    }
    
    // Clean up tab history - remove references to the closed tab
    tabHistoryRef.current = tabHistoryRef.current.filter(id => id !== tabId);
    
    removeOpenedDocument(tabId);
    
    // If this was the active tab, use history to find the previous tab
    if (activeTabId === tabId) {
      const remainingDocs = openedDocuments.filter(d => d.tabId !== tabId);
      if (remainingDocs.length > 0) {
        const remainingIds = new Set(remainingDocs.map(d => d.tabId));
        const history = tabHistoryRef.current;
        
        // Find the most recent tab in history that's still open
        let nextTabId: number | null = null;
        for (let i = history.length - 1; i >= 0; i--) {
          if (remainingIds.has(history[i])) {
            nextTabId = history[i];
            break;
          }
        }
        
        // Fallback to first remaining tab if no history
        setActiveTabId(nextTabId ?? remainingDocs[0].tabId);
      } else {
        setActiveTabId(null);
      }
    }
    
    // Save state
    saveViewerState();
  }, [
    openedDocuments,
    activeEditDocument,
    removeOpenedDocument,
    activeTabId,
    setActiveTabId,
    saveViewerState,
    releaseDocumentLock,
    clearActiveEditDocument,
    confirmCloseEditing,
  ]);

  /**
   * Navigate back to previously visited tab without closing current
   */
  const goBack = useCallback((tabId: number) => {
    const history = tabHistoryRef.current;
    const openTabIds = new Set(openedDocuments.map(d => d.tabId));
    
    // Find the most recent tab in history that:
    // 1. Is not the current tab
    // 2. Is still open
    for (let i = history.length - 1; i >= 0; i--) {
      const historyTabId = history[i];
      if (historyTabId !== tabId && openTabIds.has(historyTabId)) {
        // Remove this tab from history (we're going back to it)
        // This allows repeated back presses to work correctly
        tabHistoryRef.current = history.slice(0, i);
        setActiveTabId(historyTabId);
        return;
      }
    }
    
    // Fallback: if no history, find any other open tab
    const otherTabs = openedDocuments.filter(d => d.tabId !== tabId);
    if (otherTabs.length > 0) {
      setActiveTabId(otherTabs[0].tabId);
    }
    // If no other tabs, stay on current tab (nothing to go back to)
  }, [openedDocuments, setActiveTabId]);

  /**
   * Close all tabs
   */
  const closeAll = useCallback(async () => {
    if (activeEditDocument) {
      // Use provided confirmation callback, or fallback to native confirm
      const shouldClose = confirmCloseEditing
        ? await confirmCloseEditing({
            title: 'Close All Documents',
            description: 'A document is being edited. Close all documents anyway? Unsaved changes will be lost.',
          })
        : window.confirm('A document is being edited. Close all documents anyway?');
      
      if (!shouldClose) {
        return;
      }
    }
    
    clearOpenedDocuments();
    tabHistoryRef.current = [];
  }, [activeEditDocument, clearOpenedDocuments, confirmCloseEditing]);

  return {
    tabHistoryRef,
    viewerWidth,
    setViewerWidth,
    isResizingViewer,
    setIsResizingViewer,
    closeTab,
    closeAll,
    goBack,
    saveViewerState,
  };
}
